import { Injectable, inject } from '@angular/core';

import { CookieService } from '@fcom/core/services/cookie/cookie.service';
import { MonitoringLogger } from '@fcom/core/services/monitoring/monitoring-logger';
import { LanguageService } from '@fcom/ui-translate';

import { Consent, ConsentStatus, ElementActions, GtmConsEventData, GtmEvent } from '../../interfaces';
import { GtmService } from './gtm.service';

/**
 * 1st-party session cookie that stores the cookie-version of the
 * marketing-consent string (e.g. `moTra0_moFin1_oFli1`).
 *
 * The cookie value contains, for every marketing consent the user has
 * received a status for, the consent's short analytics code followed by
 * `1` (consent given) or `0` (not consented).
 */
export const MARKETING_CONSENT_COOKIE_NAME = 'fcom_mc';

export enum ConsentStatusDigit {
  /** Consent not given (`0`). */
  NOT_CONSENTED = '0',
  /** Consent given (`1`). */
  CONSENTED = '1',
}

/**
 * Result of mapping a marketing consent's status to its event-version
 * digit (`0` = not consented, `1` = consent given).
 */
type ConsentDigit = ConsentStatusDigit.NOT_CONSENTED | ConsentStatusDigit.CONSENTED;

/**
 * One entry of the event-version consent string: `[analyticsCode][old][new]`.
 */
export interface MarketingConsentChange {
  /** The consent before the user interaction. May be `undefined` when the user had no prior consent record. */
  previous: Consent | undefined;
  /** The consent after the user interaction. */
  current: Consent;
}

/**
 * Inputs for firing a `consEvent` to the dataLayer.
 */
export interface TrackConsentChangeArgs {
  /** GA `context` parameter, e.g. `'finnair-plus'` or `'service-forms'`. */
  context: string;
  /** GA `id` parameter, e.g. `'join-form-consent-preferences'`. */
  id: string;
  /** GA `state` parameter — optional, used to send the selected communication language. */
  state?: string;
  /** GA `action` parameter — defaults to `submit` per the spec. */
  action?: ElementActions;
  /** Consents the user has interacted with, paired with their previous status (if any). */
  changes: MarketingConsentChange[];
}

/**
 * Service that handles all GA tracking related to marketing consents:
 *
 * 1. Maintains the cookie-version of the consent string in a 1st-party
 *    session cookie.
 * 2. Emits the event-version of the consent string as a `consEvent` to
 *    the dataLayer when the user submits a consent form.
 *
 * Both strings are built from the `analyticsCode` returned by the consent
 * API. Consents without an `analyticsCode` are skipped — this means that
 * non-marketing consents (e.g. cookie consents) are naturally excluded. When a
 * marketing consent is missing its `analyticsCode`, a warning is sent to Sentry
 * so the missing mapping can be investigated.
 */
@Injectable({ providedIn: 'root' })
export class MarketingConsentTrackingService {
  private cookieService = inject(CookieService);
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private monitoringLogger = inject(MonitoringLogger);

  /**
   * Writes/updates the marketing-consent string to the 1st-party session
   * cookie. Consents without an `analyticsCode` are skipped.
   *
   * Returns `true` when the cookie was written, `false` when the input
   * produced no trackable consents (and the existing cookie, if any, is
   * left untouched).
   */
  writeConsentCookie(consents: readonly Consent[] | undefined): boolean {
    const value = this.buildCookieConsentString(consents);
    if (!value) {
      return false;
    }
    return this.cookieService.setItem(MARKETING_CONSENT_COOKIE_NAME, value, undefined, '/');
  }

  updateConsentCookie(consents: readonly Consent[] | undefined): boolean {
    const merged = parseCookieConsentString(this.cookieService.getItem(MARKETING_CONSENT_COOKIE_NAME));

    (consents ?? [])
      .filter((consent) => this.hasAnalyticsCode(consent))
      .forEach((consent) => merged.set(consent.analyticsCode, statusToDigit(consent.consentStatus)));

    if (!merged.size) {
      return false;
    }

    const value = Array.from(merged, ([code, digit]) => `${code}${digit}`)
      .sort((a, b) => a.localeCompare(b))
      .join('_');
    return this.cookieService.setItem(MARKETING_CONSENT_COOKIE_NAME, value, undefined, '/');
  }

  /**
   * Builds the cookie-version consent string for the given consents.
   *
   * Format: `[analyticsCode 1][status 1]_[analyticsCode 2][status 2]_...`
   */
  buildCookieConsentString(consents: readonly Consent[] | undefined): string {
    if (!consents?.length) {
      return '';
    }
    return consents
      .filter((consent) => this.hasAnalyticsCode(consent))
      .sort((a, b) => a.analyticsCode.localeCompare(b.analyticsCode))
      .map((consent) => `${consent.analyticsCode}${statusToDigit(consent.consentStatus)}`)
      .join('_');
  }

  /**
   * Builds the event-version consent string used as the `consStr`
   * parameter of a `consEvent`.
   *
   * Format: `[analyticsCode 1][old][new]_[analyticsCode 2][old][new]_...`
   *
   * - `01` = previously not consented, consent now given
   * - `10` = consent previously given, now removed
   * - `00` = previously not consented, no change
   * - `11` = previously consent given, no change
   *
   * Only changes whose `current` consent has an `analyticsCode` are
   * included; entries without an analytics code are skipped and reported to
   * Sentry as a warning. A missing `previous` consent is treated as not
   * consented (`0`).
   */
  buildEventConsentString(changes: readonly MarketingConsentChange[] | undefined): string {
    if (!changes?.length) {
      return '';
    }
    return changes
      .filter((change) => this.hasAnalyticsCode(change?.current))
      .sort((a, b) => (a.current.analyticsCode ?? '').localeCompare(b.current.analyticsCode ?? ''))
      .map(
        (change) =>
          `${change.current.analyticsCode}${statusToDigit(change.previous?.consentStatus)}${statusToDigit(
            change.current.consentStatus
          )}`
      )
      .join('_');
  }

  private hasAnalyticsCode(consent: Consent | undefined): consent is Consent & { analyticsCode: string } {
    if (!consent?.analyticsCode) {
      if (isMarketingConsent(consent)) {
        this.monitoringLogger.warn('Marketing consent is missing analyticsCode', {
          consentTextId: consent?.consentTextId,
          consentStatus: consent?.consentStatus,
        });
      }
      return false;
    }
    return true;
  }

  trackConsentChange({ context, id, state, action, changes }: TrackConsentChangeArgs): void {
    const consStr = this.buildEventConsentString(changes);
    if (!consStr) {
      return;
    }
    const data: GtmConsEventData = {
      context,
      id,
      action: action ?? ElementActions.SUBMIT,
      state,
      language: this.languageService.langValue,
      pagePath: getCurrentPagePath(),
      consStr,
    };
    this.gtmService.pushEventToDataLayer(GtmEvent.CONS_EVENT, data);
  }
}

function statusToDigit(status: ConsentStatus | undefined): ConsentDigit {
  return status === ConsentStatus.ACCEPTED ? ConsentStatusDigit.CONSENTED : ConsentStatusDigit.NOT_CONSENTED;
}

function parseCookieConsentString(value: string | null): Map<string, ConsentDigit> {
  const entries = new Map<string, ConsentDigit>();
  if (!value) {
    return entries;
  }
  value
    .split('_')
    .filter(Boolean)
    .forEach((entry) => {
      const digit = entry.slice(-1);
      const code = entry.slice(0, -1);
      if (code && (digit === ConsentStatusDigit.CONSENTED || digit === ConsentStatusDigit.NOT_CONSENTED)) {
        entries.set(code, digit);
      }
    });
  return entries;
}

/**
 * Prefix shared by all marketing consent text ids (e.g.
 * `fplus-marketing-flight-offers`). Non-marketing consents (such as cookie
 * consents prefixed with `f-cookies-`) are expected to have no analytics code.
 */
const MARKETING_CONSENT_TEXT_ID_PREFIX = 'fplus-marketing-';

function isMarketingConsent(consent: Consent | undefined): boolean {
  return !!consent?.consentTextId?.startsWith(MARKETING_CONSENT_TEXT_ID_PREFIX);
}

function getCurrentPagePath(): string {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  return window.location.pathname || '';
}
